import { RoleKey, RoleNameMap } from '../account/hooks/useRoles';

// Define role hierarchy (higher index = higher privilege). Guest sits at
// index 0, not appended at the end: canModifyUserRole's indexOf returns -1
// for a role absent from this list, and -1 satisfies `currentUserIndex >=
// targetRoleIndex` for every currentUserIndex, so an omitted Guest is
// (mis)treated as modifiable/assignable by anyone. Guest is also excluded
// outright in getAvailableRoles below — the hierarchy alone would still let
// it appear as a target for every role above it.
export const roleHierarchy = [
  RoleNameMap[RoleKey.GUEST],
  RoleNameMap[RoleKey.RESTRICTED],
  RoleNameMap[RoleKey.GENERAL],
  RoleNameMap[RoleKey.WORKSPACE_ADMIN],
  RoleNameMap[RoleKey.GLOBAL_ADMIN],
];

/**
 * Check if a user's role hierarchy allows them to modify/assign a target role
 * @param currentUserRole - The role of the current user
 * @param targetRole - The role being modified or assigned
 * @returns true if hierarchy allows the operation
 */
export const canModifyUserRole = (
  currentUserRole: string,
  targetRole: string
): boolean => {
  const currentUserIndex = roleHierarchy.indexOf(currentUserRole);
  const targetRoleIndex = roleHierarchy.indexOf(targetRole);

  // Can only modify/assign roles at same level or below in hierarchy
  return currentUserIndex >= targetRoleIndex;
};

/**
 * Get available roles for a user based on their role hierarchy and Global Admin visibility
 * NOTE: This only filters by hierarchy. The account.changeRole permission must be checked separately.
 * @param roleList - List of all available roles
 * @param currentUserRole - The role of the current user
 * @param showGlobalAdminRole - Whether to show Global Admin role
 * @returns Filtered list of roles based on hierarchy
 */
export const getAvailableRoles = (
  roleList: Array<{ id: string; name: string }>,
  currentUserRole?: string,
  showGlobalAdminRole = false
): Array<{ id: string; name: string }> => {
  let filteredRoles = roleList;

  // Filter based on current user's role hierarchy
  if (currentUserRole) {
    filteredRoles = filteredRoles.filter((role) =>
      canModifyUserRole(currentUserRole, role.name)
    );
  }

  // Filter out Global Admin role if not allowed
  if (!showGlobalAdminRole) {
    filteredRoles = filteredRoles.filter(
      (role) => role.name !== 'Global Admin'
    );
  }

  // Guest is minted by the reply hook (plan 0030), never assigned by a human
  // through this picker — excluded outright, the same way Global Admin is
  // gated separately from hierarchy above. RoleNameMap[RoleKey.GUEST], not a
  // hardcoded string: roleHierarchy above already derives from it, and a
  // second, independent 'Guest' literal here could silently drift from it if
  // the display name ever changed, reopening this task's own bug on this
  // exclusion instead.
  filteredRoles = filteredRoles.filter(
    (role) => role.name !== RoleNameMap[RoleKey.GUEST]
  );

  return filteredRoles;
};
